<template>
  <ag-grid-vue
    v-if="loadedGridOptions"
    v-show="loaded"
    :key="componentId"
    v-shortkey="shortKey.keys"
    class="vt-grid ag-theme-balham"
    :grid-options="finalGridOptions"
    :column-defs="finalColumnDefs"
    :row-data="rowData"
    :detail-cell-renderer="detailCellRenderer"
    :detail-row-height="finalDetailRowHeight"
    :framework-components="finalFrameworkComponents"
    :master-detail="masterDetail"
    :detail-cell-renderer-params="finalDetailCellRendererParams"
    :pivot-mode="pivotMode"
    v-bind="$attrs"
    :process-cell-for-clipboard="processCellForClipboard"
    @selection-changed="onSelectionChanged"
    @modelUpdated="onModelUpdated"
    @shortkey.native="shortKey.callFn"
  />
</template>

<script>

import { isEqual, pick } from 'lodash'
import customConfigResource from '@/api/resources/ui/CustomConfigResource'
import { AgGridVue } from '@ag-grid-community/vue'
import { baseGridOptions } from '@/grid/base-grid-options'
import baseFrameworkComponents from '@/grid/baseFrameworkComponents'
import CheckboxHeader from '@/grid/CheckboxHeader'
import { AgGridCallbackEventType } from '@/shared/models/ag-grid.model'

export default {
    name: 'VtGrid',
    components: { AgGridVue },
    props: {
        shortKey: {
            type: Object,
            default: () => {
                return {
                    keys: null,
                    callFn: null
                }
            }
        },
        gridOptions: { type: Object, default: () => ({}) },
        masterDetail: { type: Boolean, default: false },
        detailCellRendererParams: { type: Object },
        columnDefs: { type: Array },
        frameworkComponents: { type: Object },
        rowData: { type: Array, default: null },
        useDetailCustomConfig: { type: Boolean, default: false },
        componentCode: { type: String },
        componentId: { type: String },
        detailCellRenderer: { type: String },
        detailRowHeight: { type: Number },
        autosizeColumns: { type: Boolean, default: true },
        pivotMode: { type: Boolean, default: false }
    },
    data() {
        return {
            loaded: false,
            gridApi: null,
            columnApi: null,
            customConfig: {},
            loadedGridOptions: null
        }
    },
    computed: {
        finalGridOptions() {
            return {
                ...this.loadedGridOptions,
                ...this.gridOptions,
                getContextMenuItems: this.onGetContextMenuItems,
                onGridReady: this.onGridReady,
                onColumnVisible: this.onColumnVisible,
                onColumnPinned: this.onColumnPinned,
                onDragStopped: this.onDragStopped,
                onFirstDataRendered: this.onFirstDataRendered,
                suppressCopyRowsToClipboard: true
            }
        },
        finalColumnDefs() {
            return this.columnDefs ?? this.finalGridOptions.columnDefs ?? []
        },
        finalDetailCellRendererParams() {
            if (!this.useDetailCustomConfig || !this.detailCellRendererParams) {
                return this.detailCellRendererParams
            }
            const detailCellRendererParams = {
                ...this.detailCellRendererParams,
                detailGridOptions: {
                    ...this.loadedGridOptions,
                    onGridReady: this.onSubGridReady,
                    onColumnVisible: this.changedSubGridState,
                    onColumnPinned: this.changedSubGridState,
                    onDragStopped: this.onSubGridDragStopped
                }
            }
            if (this.detailCellRendererParams.detailGridOptions) {
                Object.assign(detailCellRendererParams.detailGridOptions, this.detailCellRendererParams.detailGridOptions)
            }
            return detailCellRendererParams
        },
        finalDetailRowHeight() {
            return this.detailRowHeight ?? this.finalGridOptions.detailRowHeight ?? 260
        },
        finalFrameworkComponents() {
            return {
                ...baseFrameworkComponents,
                customCheckboxHeader: CheckboxHeader,
                ...this.frameworkComponents
            }
        },
        subGridConfig() {
            return this.$store.getters.currentCustomConfigGrid?.config?.subGrid
        }
    },
    watch: {
        async componentCode() {
            await this.loadCustomConfig()
        }
    },
    created() {
        this.loadedGridOptions = baseGridOptions()
    },
    beforeDestroy() {
        this.$store.commit('updateCurrentCustomConfigGrid', null)

        if (this.gridApi) {
            this.gridApi.removeEventListener(AgGridCallbackEventType.WARN, this.onServerWarnCallback)
            this.gridApi.removeEventListener(AgGridCallbackEventType.FAIL, this.onServerFailCallback)
            this.gridApi.removeEventListener(AgGridCallbackEventType.END, this.onServerEndCallback)
            this.gridApi.destroy()
        }
    },
    methods: {
        onFirstDataRendered() {
            this.gridApi.redrawRows()
            this.$emit('onFirstDataRendered')
        },
        async onGridReady(event) {
            this.gridApi = event.api
            this.columnApi = event.columnApi

            this.gridApi.addEventListener(AgGridCallbackEventType.WARN, this.onServerWarnCallback)
            this.gridApi.addEventListener(AgGridCallbackEventType.FAIL, this.onServerFailCallback)
            this.gridApi.addEventListener(AgGridCallbackEventType.END, this.onServerEndCallback)

            try {
                if (this.gridOptions.onGridReady) {
                    this.gridOptions.onGridReady(event)
                }
                await this.loadCustomConfig()
            } finally {
                this.loaded = true
            }
        },
        onServerWarnCallback(event) {
            this.$_showWarningModal(event.error)
        },
        onServerFailCallback(error) {
            this.$_showErrorModalFromErrorResponse(error)
        },
        onServerEndCallback(event) {
            this.$emit('end-callback', event)
        },
        async loadCustomConfig() {
            if (this.componentCode) {
                const customConfig = await customConfigResource.get(this.componentCode)
                if (!customConfig) {
                    if (this.columnApi) {
                        this.columnApi.resetColumnState()
                    }
                } else {
                    this.customConfig = customConfig
                    if (customConfig.config?.columnState && this.columnApi) {
                        this.columnApi.applyColumnState({ state: customConfig.config.columnState, applyOrder: true })
                    }
                }
            }
        },
        autosizeAllColumns(columnApi) {
            if (columnApi && this.autosizeColumns) {
                const allColumnIds = []
                columnApi.getAllColumns()?.forEach((column) => {
                    if (!column.colDef.cellRendererFramework) {
                        allColumnIds.push(column.colId)
                    }
                })
                columnApi.autoSizeColumns(allColumnIds)
            }
        },
        suppressPaginationPanel(option) {
            [...document.getElementsByClassName('ag-paging-panel')].forEach(element => {
                element.style.visibility = option ? 'hidden' : 'visible'
            })
        },
        onModelUpdated({ api, columnApi }) { // ModelUpdatedEvent
            if (api.getDisplayedRowCount() === 0) {
                api.showNoRowsOverlay()
                this.suppressPaginationPanel(true)
            } else {
                api.hideOverlay()
                api.clearFocusedCell()
                this.autosizeAllColumns(columnApi)
                this.suppressPaginationPanel(false)
            }
            this.$emit('model-updated', api)
        },
        async onColumnVisible({ columnApi }) {
            await this.saveCustomConfig()
            this.autosizeAllColumns(columnApi)
            this.$emit('column-visible')
        },
        async onColumnPinned() {
            await this.saveCustomConfig()
        },
        async onDragStopped(params) {
            const newState = params.columnApi.getColumnState()
            const oldState = this.customConfig?.config?.columnState
            if (!oldState || this.columnOrderHasChanged(newState, oldState)) {
                await this.saveCustomConfig()
            }
            if (this.gridOptions.onDragStopped) {
                this.gridOptions.onDragStopped(params)
            }
        },
        columnOrderHasChanged(newColumnState, oldColumnState) {
            const newOrder = newColumnState.map(i => i.colId)
            const oldOrder = oldColumnState.map(i => i.colId)
            return !isEqual(newOrder, oldOrder)
        },
        async saveCustomConfig() {
            if (this.componentCode) {
                const config = {
                    ...this.customConfig.config,
                    columnState: this.columnApi.getColumnState().map(i => pick(i, 'colId', 'hide', 'pinned'))
                }
                const customConfig = await this.$_adviceSaveUiComponent(this.componentCode, config)
                if (customConfig) {
                    this.customConfig = {
                        ...this.customConfig.config,
                        ...customConfig
                    }
                }
            }
        },
        onSubGridReady(params) {
            if (this.subGridConfig) {
                params.columnApi.applyColumnState({ state: this.subGridConfig.columnState, applyOrder: true })
            }
        },
        async onSubGridDragStopped(params) {
            const newState = params.columnApi.getColumnState()
            const oldState = this.subGridConfig && this.subGridConfig.columnState
            if (!oldState || this.columnOrderHasChanged(newState, oldState)) {
                await this.changedSubGridState(params)
            }
        },
        async changedSubGridState(params) {
            await this.saveSubGridState(params)
        },
        async saveSubGridState(params) {
            const config = {
                ...this.customConfig.config,
                subGrid: {
                    ...this.subGridConfig,
                    columnState: params.columnApi.getColumnState().map(i => pick(i, 'colId', 'hide', 'pinned'))
                }
            }
            await this.$_adviceSaveUiComponent(this.componentCode, config)
        },
        onSelectionChanged(event) {
            this.$emit('selection-changed', event)
        },
        onGetContextMenuItems(params) {
            if (params.node) {
                if (!params.node.isSelected()) {
                    params.node.setSelected(true, true)
                    params.api.refreshHeader()

                    // force synchronous event calling immediately
                    if (typeof this.gridOptions.onSelectionChanged === 'function') {
                        const event = {
                            api: params.api,
                            columnApi: params.columnApi,
                            type: 'selectionChanged'
                        } // SelectionChangedEvent
                        this.gridOptions.onSelectionChanged(event)
                    }
                }

                if (typeof this.gridOptions.getContextMenuItems === 'function') {
                    return this.gridOptions.getContextMenuItems(params)
                }
            }
            return []
        },
        processCellForClipboard(params) {
            return this.$_processCellOutput(params.column, params.value, params?.node)
        }
    }
}

</script>

<style scoped lang="scss">
    .vt-grid {
        width: 100%;
    }
</style>
